"use client";

import { AnyTranslationsFn } from "@/types/locale";
import React from "react";
import { ContentLocalizableEditBlock } from "@components/common/content/content-localizable-edit-block";
import { zt } from "@lib/forms/zt";
import { DesignSystemFixtureMetadata } from "@features/design-system/design-system-types";
import { FixtureInvariantMeta } from "@features/design-system/ui/fixture/fixture-invariant-meta";
import { FixtureLibsUsed } from "@features/design-system/ui/fixture/fixture-libs-used";
import { FixtureMetadata } from "@features/design-system/ui/fixture/fixture-metadata";
import { FixturePropertyImpactTable } from "@features/design-system/ui/fixture/fixture-property-impact-table";
import {
  FixturePropsTable,
  PropertyDefinition,
} from "@features/design-system/ui/fixture/fixture-props-table";
import { FixtureSection } from "@features/design-system/ui/fixture/fixture-section";
import { FixtureWrapper } from "@features/design-system/ui/fixture/fixture-wrapper";
import { ActivityEntity, ActivityInputView } from "@features/proposals/types/activity";

// Хелпер для локализуемых полей (LocaleMessageType)
const R = (ru: string, en = ru) => ({ ru, en });

// Моки тестовых данных для разных состояний
const baseActivity = {
  id: 1,
  rights: { canPatch: true },
  content: {
    data: {
      title: R(
        "Введение в архитектуру микросервисов",
        "Introduction to Microservices Architecture",
      ),
    },
    version: 1,
    updatedAt: new Date().toISOString(),
  },
  updatedAt: new Date().toISOString(),
} as unknown as ActivityEntity;

const activityWithLongContent = {
  ...baseActivity,
  id: 2,
  content: {
    data: {
      fullDescription: R(
        "<p>Очень длинное описание с HTML-разметкой. </p><p>Несколько абзацев для проверки работы rich editor.</p><ul><li>Пункт списка 1</li><li>Пункт списка 2</li></ul>",
        "<p>Very long description with HTML markup. </p><p>Multiple paragraphs to test rich editor.</p><ul><li>List item 1</li><li>List item 2</li></ul>",
      ),
    },
    version: 1,
    updatedAt: new Date().toISOString(),
  },
} as unknown as ActivityEntity;

const activityWithEmptyContent: ActivityEntity = {
  ...baseActivity,
  id: 3,
  content: {
    data: {
      shortDescription: R("", ""),
    },
    version: 1,
    updatedAt: new Date().toISOString(),
  },
} as unknown as ActivityEntity;

const activityNoPermissions = {
  ...baseActivity,
  id: 4,
  rights: { canPatch: false },
} as ActivityEntity;

const activityOnlyRussian = {
  ...baseActivity,
  id: 5,
  content: {
    data: {
      title: { ru: "Только на русском языке", en: undefined },
    },
    version: 1,
    updatedAt: new Date().toISOString(),
  },
} as unknown as ActivityEntity;

// Мок-функция patch (имитация API-вызова)
const mockPatch = async (body: Partial<ActivityInputView>): Promise<ActivityEntity> => {
  console.log("Mock patch called with:", body);
  // Имитация задержки сети
  await new Promise((resolve) => setTimeout(resolve, 300));
  // Возвращаем обновлённую сущность (в реальности это ответ сервера)
  return {
    ...baseActivity,
    ...body,
    content: {
      ...baseActivity.content,
      data: {
        ...(baseActivity.content?.data || {}),
        ...(body.content?.data || {}),
      },
      version: (baseActivity.content?.version || 0) + 1,
      updatedAt: new Date().toISOString(),
    },
    updatedAt: new Date().toISOString(),
  } as unknown as ActivityEntity;
};

// Мок-функция валидации (Zod-схема для поля title)
const getTitleValidationSchema = (t: AnyTranslationsFn) =>
  zt.contentStringField(t, "title", true, 3);

const getDescriptionValidationSchema = (t: AnyTranslationsFn) =>
  zt.contentStringField(t, "fullDescription", false);

// Документация свойств публичного API
const generalProps: PropertyDefinition[] = [
  {
    name: "name",
    type: "string",
    defaultValue: "undefined",
    description:
      "Путь к локализованному полю в структуре данных (например, 'content.data.title'). Определяет, какое поле будет редактироваться.",
  },
  {
    name: "richEditor",
    type: "boolean",
    defaultValue: "false",
    description:
      "Включает rich text editor (TipTap) для редактирования HTML-контента. Используется для полей с разметкой (fullDescription, plan).",
  },
  {
    name: "patch",
    type: "(body: Partial<ActivityInputView>) => Promise<ActivityEntity>",
    defaultValue: "undefined",
    description:
      "Асинхронная функция для отправки изменений на сервер. Получает минимальный набор изменённых полей (optimistic dirty tracking).",
  },
  {
    name: "initData",
    type: "ActivityEntity",
    defaultValue: "undefined",
    description:
      "Начальные данные сущности. Используются для инициализации формы и определения доступных локалей.",
  },
  {
    name: "getValidationSchema",
    type: "(t: AnyTranslationsFn) => ZodObject",
    defaultValue: "undefined",
    description:
      "Функция, возвращающая Zod-схему валидации для поля. Сообщения об ошибках локализуются через next-intl.",
  },
  {
    name: "title",
    type: "string",
    defaultValue: "undefined",
    description: "Заголовок модального окна редактирования.",
  },
  {
    name: "description",
    type: "string",
    defaultValue: "undefined",
    description: "Описание назначения поля в модальном окне.",
  },
  {
    name: "notes",
    type: "string",
    defaultValue: "undefined",
    description:
      "Дополнительные заметки для редактора (отображаются в blockquote). Не сохраняются, только для контекста.",
  },
  {
    name: "required",
    type: "boolean",
    defaultValue: "false",
    description:
      "Указывает, что поле обязательно для заполнения. Влияет на валидацию и UI (отображается астериск).",
  },
];

const ContentLocalizableEditBlockFixture = () => (
  <>
    {/* 1. Описание */}
    <FixtureSection title="1. Описание" id="about">
      <div className="space-y-4">
        <p>
          Компонент <strong>ContentLocalizableEditBlock</strong> предоставляет интерфейс для
          редактирования локализованных полей сущности Activity в модальном окне. Поддерживает
          валидацию с помощью Zod, отслеживание изменений (optimistic dirty tracking), обработку
          конфликтов при одновременном редактировании и минимальную отправку изменений на сервер.
        </p>
        <p>
          <strong>Когда использовать:</strong> В разделе Activity/Edit для редактирования
          многоязычных полей заявки (title, shortDescription, fullDescription, plan).
        </p>
        <p>
          <strong>Ключевые особенности:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Многоязычное редактирование: переключение между режимами &quot;одна локаль&quot; и
            &quot;все локали&quot;
          </li>
          <li>
            Инструменты копирования между локалями: кнопки &quot;Скопировать в EN&quot; и
            &quot;Скопировать из EN&quot;
          </li>
          <li>Rich text editor (TipTap) для полей с HTML-разметкой (опционально)</li>
          <li>Валидация в реальном времени с локализованными сообщениями об ошибках</li>
          <li>
            Optimistic dirty tracking: отправка только изменённых полей (через
            prepareModifiedValues)
          </li>
          <li>
            Обработка конфликтов: если сервер изменил поле одновременно с пользователем,
            отображается ConflictAlert с возможностью сброса
          </li>
          <li>
            Автофокус на первое поле в режиме &quot;все локали&quot; или на единственное поле в
            режиме &quot;одна локаль&quot;
          </li>
          <li>
            Кнопка сброса изменений (Reset) и автоматический reset при закрытии без сохранения
          </li>
          <li>Защита прав доступа: компонент не рендерится, если rights.canPatch = false</li>
        </ul>
      </div>
    </FixtureSection>

    {/* 2. Meta */}
    <FixtureSection title="2. Meta" id="meta">
      <FixtureMetadata
        componentName="ContentLocalizableEditBlock"
        sourcePath="src/components/common/content/content-localizable-edit-block.tsx"
        domain="Common → Content → Proposals / Activity / Edit"
        dependencies={[
          {
            path: "@components/common/content/content-edit-block",
            description: "Базовый контейнер для блоков редактирования с формой и валидацией",
          },
          {
            path: "@components/common/buttons/switch-locale-toggle",
            description: "Переключатель языков с режимом 'all locales'",
          },
          {
            path: "@components/common/forms/form-text-field",
            description: "Текстовое поле формы с поддержкой rich editor",
          },
          {
            path: "@components/common/forms/form-text-input-tools",
            description: "Инструменты для работы с текстовым вводом (копирование между локалями)",
          },
          {
            path: "@lib/content",
            description: "contentTools.getNestedValue для извлечения вложенных значений",
          },
          {
            path: "react-hook-form",
            description: "Управление состоянием формы и валидация",
          },
          {
            path: "@hookform/resolvers/zod",
            description: "Интеграция Zod с React Hook Form",
          },
          {
            path: "next-intl",
            description: "Хуки useLocale и useTranslations для i18n",
          },
        ]}
        version="1.0.0"
      />
    </FixtureSection>

    {/* 3. Бизнес-контекст */}
    <FixtureSection title="3. Бизнес‑контекст" id="business-context">
      <div className="space-y-4">
        <p>
          <strong>Роли и сценарии использования:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Спикер (Activity Edit):</strong> Редактирование локализованных полей своей
            заявки (title, description, plan). Ожидаемая ценность: удобное переключение между
            языками, инструменты копирования для ускорения перевода, автосохранение при закрытии,
            валидация перед отправкой.
          </li>
          <li>
            <strong>Куратор/Организатор:</strong> Редактирование содержания заявок для исправления
            ошибок или дополнения информации. Ожидаемая ценность: режим &quot;все локали&quot; для
            одновременного просмотра и редактирования обеих версий, быстрое копирование текста между
            языками.
          </li>
          <li>
            <strong>Редактор контента:</strong> Работа над качеством локализации. Ожидаемая
            ценность: rich text editor для форматирования, валидация минимальной длины текста,
            предотвращение случайного удаления важного контента.
          </li>
          <li>
            <strong>Переводчик:</strong> Перевод содержания заявок с одного языка на другой.
            Ожидаемая ценность: режим &quot;все локали&quot; для сравнения оригинала и перевода,
            кнопки копирования для использования оригинала как базы.
          </li>
        </ul>
        <p>
          <strong>Ключевые ограничения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Компонент рендерится только если <code>initData.rights?.canPatch = true</code>. Это
            защищает от несанкционированного редактирования.
          </li>
          <li>
            Доступные локали определяются автоматически из <code>initData</code> по пути{" "}
            <code>name</code> (например, content.data.title → {`{ ru, en }`}). Если поле
            отсутствует, используется дефолтный набор {`{ ru: "", en: "" }`}.
          </li>
          <li>
            Отправка данных происходит только для изменённых полей благодаря{" "}
            <code>formState.dirtyFields</code>. Это минимизирует риск конфликтов и уменьшает
            нагрузку на сервер.
          </li>
          <li>
            При обнаружении конфликта (сервер изменил те же поля) отображается{" "}
            <code>ConflictAlert</code> с списком конфликтующих полей. Пользователь может сбросить
            форму к актуальному состоянию сервера.
          </li>
          <li>
            Кнопка &quot;Сохранить&quot; активна только если форма изменена (
            <code>isDirty = true</code>) и валидна (<code>isValid = true</code>).
          </li>
          <li>
            При закрытии модального окна без сохранения форма автоматически сбрасывается к{" "}
            <code>initData</code> (discards local changes).
          </li>
          <li>
            Режим <code>richEditor = true</code> включает TipTap для полей с HTML-разметкой.
            Валидация учитывает только видимый текст (через <code>getSanitizedContent</code>),
            игнорируя HTML-теги.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 4. Пропсы и типы */}
    <FixtureSection title="4. Пропсы и типы" id="props-types">
      <FixturePropsTable properties={generalProps} />
      <div className="mt-4 space-y-3">
        <FixturePropertyImpactTable
          properties={[
            {
              propertyPath: "name",
              type: "string",
              renderImpact:
                "Определяет путь к редактируемому полю в initData и формирует имена полей формы (name.ru, name.en).",
              missingBehavior: "Компонент не сможет корректно работать без указания name.",
            },
            {
              propertyPath: "richEditor",
              type: "boolean",
              renderImpact:
                "Включает TipTap rich text editor вместо обычного textarea. Позволяет форматировать текст (жирный, курсив, списки, ссылки).",
              missingBehavior: "По умолчанию false, используется обычное textarea.",
            },
            {
              propertyPath: "patch",
              type: "function",
              renderImpact:
                "Асинхронная функция для отправки изменений на сервер. Получает минимальный payload с изменёнными полями.",
              missingBehavior: "Компонент не может сохранить изменения без функции patch.",
            },
            {
              propertyPath: "initData",
              type: "ActivityEntity",
              renderImpact:
                "Начальные данные для формы. Используются для reset и определения доступных локалей.",
              missingBehavior: "Форма не может быть инициализирована без initData.",
            },
            {
              propertyPath: "initData.rights?.canPatch",
              type: "boolean",
              renderImpact:
                "Определяет, может ли пользователь редактировать поле. При false компонент возвращает null.",
              missingBehavior: "Если права отсутствуют, считается false.",
            },
            {
              propertyPath: "getValidationSchema",
              type: "function",
              renderImpact:
                "Возвращает Zod-схему для валидации поля. Сообщения об ошибках локализуются через t().",
              missingBehavior: "Без валидации форма может отправить некорректные данные.",
            },
            {
              propertyPath: "required",
              type: "boolean",
              renderImpact:
                "Отображает астериск (*) рядом с полем и включает валидацию на обязательность.",
              missingBehavior: "По умолчанию false, поле считается опциональным.",
            },
            {
              propertyPath: "title",
              type: "string",
              renderImpact: "Заголовок модального окна редактирования.",
              missingBehavior: "Модальное окно отображается без заголовка.",
            },
            {
              propertyPath: "notes",
              type: "string",
              renderImpact:
                "Дополнительные заметки в blockquote для контекста. Не сохраняются на сервере.",
              missingBehavior: "Заметки не отображаются.",
            },
          ]}
        />
      </div>
    </FixtureSection>

    {/* 5. Инварианты */}
    <FixtureSection title="5. Инварианты" id="invariants">
      {/* 5.1 Базовый */}
      <FixtureSection title="5.1 Базовое редактирование" id="invariant-base" variant="inner">
        <FixtureInvariantMeta
          id="base-edit"
          name="Стандартное редактирование локализованного поля"
          goal="Проверка базового сценария редактирования с переключением между локалями."
          preconditions="initData содержит локализованное поле с ru и en, rights.canPatch = true."
          inputData={[
            "name: 'content.data.title'",
            "initData.content.data.title: { ru: 'текст', en: 'text' }",
            "patch: async function",
            "getValidationSchema: Zod-схема с минимальной длиной 3 символа",
            "required: true",
          ]}
          expectedResult="Отображается кнопка Edit, при клике открывается модальное окно с двумя полями (ru/en). Переключатель языка позволяет выбрать ru, en или 'Все'. Кнопка 'Сохранить' активна только при изменении и валидности."
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "name",
              type: "string",
              value: '"content.data.title"',
              description: "Путь к полю title",
            },
            {
              name: "initData",
              type: "ActivityEntity",
              value: "{ content.data.title: { ru, en } }",
              description: "Начальные данные",
            },
            {
              name: "required",
              type: "boolean",
              value: "true",
              description: "Обязательное поле",
            },
          ]}
        />
        <FixtureWrapper label="Базовое редактирование" variant="default">
          <div className="rounded border p-4">
            <p className="mb-2 text-sm text-muted-foreground">
              Кликните на кнопку Edit для открытия модального окна редактирования
            </p>
            <ContentLocalizableEditBlock
              name="content.data.title"
              patch={mockPatch}
              initData={baseActivity}
              getValidationSchema={getTitleValidationSchema}
              title="Заголовок активности"
              description="Локализованное название доклада (обязательное поле)"
              required
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.2 Rich Editor */}
      <FixtureSection title="5.2 Rich Text Editor" id="invariant-rich-editor" variant="inner">
        <FixtureInvariantMeta
          id="rich-editor"
          name="Редактирование с rich text editor"
          goal="Проверка работы TipTap rich editor для полей с HTML-разметкой."
          preconditions="richEditor = true, поле содержит HTML-контент."
          inputData={[
            "richEditor: true",
            "name: 'content.data.fullDescription'",
            "initData.content.data.fullDescription: { HTML-контент }",
          ]}
          expectedResult="В модальном окне отображается TipTap editor с панелью инструментов форматирования. Валидация учитывает только видимый текст, игнорируя HTML-теги."
          edgeCases="HTML-теги не учитываются при подсчёте символов для min/max валидации."
        />
        <FixtureWrapper label="Rich Editor" variant="default">
          <div className="rounded border p-4">
            <p className="mb-2 text-sm text-muted-foreground">
              Rich editor с панелью форматирования (TipTap)
            </p>
            <ContentLocalizableEditBlock
              name="content.data.fullDescription"
              patch={mockPatch}
              initData={activityWithLongContent}
              getValidationSchema={getDescriptionValidationSchema}
              title="Полное описание"
              description="Детальное описание доклада с HTML-разметкой"
              richEditor
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.3 Режим "Все локали" */}
      <FixtureSection title="5.3 Режим 'Все локали'" id="invariant-all-locales" variant="inner">
        <FixtureInvariantMeta
          id="all-locales-mode"
          name="Одновременное редактирование всех локалей"
          goal="Проверка режима 'Все' для одновременного просмотра и редактирования ru и en."
          preconditions="Переключатель языка установлен в режим 'Все'."
          inputData={["locale: 'all'"]}
          expectedResult="Отображаются оба поля (ru и en) друг под другом с метками языка. Кнопки копирования позволяют перенести текст из одного языка в другой. Автофокус на первом поле (ru)."
          edgeCases="В режиме 'Все' каждое поле имеет свои инструменты копирования (вверх/вниз)."
        />
        <FixtureWrapper label="Режим 'Все локали'" variant="default">
          <div className="rounded border p-4">
            <p className="mb-2 text-sm text-muted-foreground">
              Откройте модальное окно и переключите на &quot;Все&quot; для одновременного
              редактирования
            </p>
            <ContentLocalizableEditBlock
              name="content.data.title"
              patch={mockPatch}
              initData={baseActivity}
              getValidationSchema={getTitleValidationSchema}
              title="Заголовок (все локали)"
              description="Одновременное редактирование русской и английской версий"
              required
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.4 Пустые данные */}
      <FixtureSection title="5.4 Пустые данные" id="invariant-empty" variant="inner">
        <FixtureInvariantMeta
          id="empty-content"
          name="Редактирование пустого поля"
          goal="Проверка поведения при пустых начальных данных."
          preconditions="initData содержит пустые строки для обоих языков."
          inputData={["initData.content.data.shortDescription: { ru: '', en: '' }"]}
          expectedResult="Поля формы пустые, но доступны для редактирования. Валидация работает корректно (для optional полей не блокирует сохранение)."
          edgeCases="Для required полей валидация должна блокировать сохранение пустых значений."
        />
        <FixtureWrapper label="Пустые данные" variant="default">
          <div className="rounded border p-4">
            <p className="mb-2 text-sm text-muted-foreground">Поле с пустыми начальными данными</p>
            <ContentLocalizableEditBlock
              name="content.data.shortDescription"
              patch={mockPatch}
              initData={activityWithEmptyContent}
              getValidationSchema={(t) => zt.contentStringField(t, "shortDescription", false)}
              title="Краткое описание"
              description="Опциональное поле"
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.5 Нет прав на редактирование */}
      <FixtureSection
        title="5.5 Нет прав на редактирование"
        id="invariant-no-permissions"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="no-permissions"
          name="Скрытие компонента при отсутствии прав"
          goal="Проверка защиты прав доступа: компонент не рендерится при canPatch = false."
          preconditions="initData.rights?.canPatch = false."
          inputData={["rights.canPatch: false"]}
          expectedResult="Компонент возвращает null, кнопка Edit не отображается."
          edgeCases="Это критическое бизнес-правило безопасности — без прав UI не должен позволять редактирование."
        />
        <FixtureWrapper label="Нет прав" variant="default">
          <div className="rounded border border-dashed p-4">
            <p className="text-sm text-muted-foreground">
              Ожидаемо пусто: при rights.canPatch = false компонент не рендерится
            </p>
            <ContentLocalizableEditBlock
              name="content.data.title"
              patch={mockPatch}
              initData={activityNoPermissions}
              getValidationSchema={getTitleValidationSchema}
              title="Заголовок"
              required
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.6 Только одна локаль */}
      <FixtureSection title="5.6 Только одна локаль" id="invariant-single-locale" variant="inner">
        <FixtureInvariantMeta
          id="single-locale"
          name="Поле с контентом только на одном языке"
          goal="Проверка поведения при наличии контента только на ru (en = undefined)."
          preconditions="initData.content.data.title = { ru: 'текст', en: undefined }"
          inputData={["title.en: undefined"]}
          expectedResult="Переключатель языка отображает только ru. Поле en либо пустое, либо скрыто (в зависимости от режима)."
          edgeCases="Компонент должен корректно обрабатывать undefined в одной из локалей."
        />
        <FixtureWrapper label="Только русский" variant="default">
          <div className="rounded border p-4">
            <p className="mb-2 text-sm text-muted-foreground">
              Контент доступен только на русском языке
            </p>
            <ContentLocalizableEditBlock
              name="content.data.title"
              patch={mockPatch}
              initData={activityOnlyRussian}
              getValidationSchema={getTitleValidationSchema}
              title="Заголовок (только RU)"
              required
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.7 С notes */}
      <FixtureSection title="5.7 С дополнительными заметками" id="invariant-notes" variant="inner">
        <FixtureInvariantMeta
          id="with-notes"
          name="Отображение контекстуальных заметок"
          goal="Проверка отображения notes в blockquote для дополнительного контекста."
          preconditions="notes передан как строка."
          inputData={["notes: 'Это поле используется для...''"]}
          expectedResult="В модальном окне под заголовком отображается блок с заметками в blockquote (голубая левая граница, курсивный текст)."
          edgeCases="Заметки не сохраняются на сервере, только для UI."
        />
        <FixtureWrapper label="С заметками" variant="default">
          <div className="rounded border p-4">
            <p className="mb-2 text-sm text-muted-foreground">
              Откройте модальное окно для просмотра заметок
            </p>
            <ContentLocalizableEditBlock
              name="content.data.title"
              patch={mockPatch}
              initData={baseActivity}
              getValidationSchema={getTitleValidationSchema}
              title="Заголовок с заметками"
              description="Поле с дополнительным контекстом"
              notes="Это поле используется для отображения названия доклада на сайте конференции. Минимальная длина — 3 символа, максимальная — не ограничена. Рекомендуется использовать ёмкое и точное описание темы."
              required
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.8 Галерея */}
      <FixtureSection title="5.8 Галерея состояний" id="invariant-gallery" variant="inner">
        <FixtureInvariantMeta
          id="state-gallery"
          name="Сравнение всех основных состояний"
          goal="Визуальная регрессия для различных комбинаций пропсов."
          preconditions="Нет."
          inputData={["Набор блоков с разными комбинациями"]}
          expectedResult="Отображаются все варианты: базовый, rich editor, пустые данные, нет прав, только одна локаль."
          edgeCases="Нет."
        />
        <FixtureWrapper label="Галерея" variant="default">
          <div className="flex flex-col gap-4">
            <div className="rounded border p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Базовый (required)</p>
              <ContentLocalizableEditBlock
                name="content.data.title"
                patch={mockPatch}
                initData={baseActivity}
                getValidationSchema={getTitleValidationSchema}
                title="Заголовок"
                required
              />
            </div>
            <div className="rounded border p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Rich Editor (optional)
              </p>
              <ContentLocalizableEditBlock
                name="content.data.fullDescription"
                patch={mockPatch}
                initData={activityWithLongContent}
                getValidationSchema={getDescriptionValidationSchema}
                title="Полное описание"
                richEditor
              />
            </div>
            <div className="rounded border p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Пустые данные</p>
              <ContentLocalizableEditBlock
                name="content.data.shortDescription"
                patch={mockPatch}
                initData={activityWithEmptyContent}
                getValidationSchema={(t) => zt.contentStringField(t, "shortDescription", false)}
                title="Краткое описание"
              />
            </div>
            <div className="rounded border border-dashed p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Нет прав (не рендерится)
              </p>
              <ContentLocalizableEditBlock
                name="content.data.title"
                patch={mockPatch}
                initData={activityNoPermissions}
                getValidationSchema={getTitleValidationSchema}
                title="Недоступно"
              />
            </div>
          </div>
        </FixtureWrapper>
      </FixtureSection>
    </FixtureSection>

    {/* 6. Примеры и edge-cases */}
    <FixtureSection title="6. Примеры и крайние случаи" id="edge-cases">
      <div className="space-y-4">
        <p>
          <strong>Полнота данных:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Полные данные:</strong> initData с ru и en → полноценное редактирование с
            переключением языков.
          </li>
          <li>
            <strong>Только одна локаль:</strong> initData с ru или en (вторая undefined) →
            переключатель показывает только доступный язык.
          </li>
          <li>
            <strong>Пустые данные:</strong> {`initData.content.data.field = { ru: '', en: '' }`} →
            поля пустые, но доступны для редактирования.
          </li>
          <li>
            <strong>Отсутствует вложенное поле:</strong> initData не содержит указанный path →
            используется дефолтный {`{ ru: '', en: '' }`}.
          </li>
          <li>
            <strong>Нет прав:</strong> rights.canPatch = false → компонент возвращает null.
          </li>
        </ul>
        <p>
          <strong>Валидация:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Минимальная длина (min): для required полей с min=3 пустая строка или 1-2 символа →
            ошибка валидации, кнопка &quot;Сохранить&quot; неактивна.
          </li>
          <li>
            HTML-контент: для richEditor валидация учитывает только видимый текст (через
            getSanitizedContent), игнорируя HTML-теги.
          </li>
          <li>
            Локализованные сообщения об ошибках: Zod-схема использует t() для перевода ошибок на
            текущий язык интерфейса.
          </li>
        </ul>
        <p>
          <strong>Обработка конфликтов:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Если сервер изменил те же поля одновременно с пользователем → отображается ConflictAlert
            с списком конфликтующих полей.
          </li>
          <li>
            Пользователь может сбросить форму к актуальному состоянию сервера через кнопку в
            ConflictAlert.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 7. Accessibility */}
    <FixtureSection title="7. Доступность" id="accessibility">
      <ul className="ml-6 list-disc space-y-1">
        <li>Кнопка Edit доступна с клавиатуры: Tab для фокуса, Enter/Space для активации.</li>
        <li>
          Модальное окно управляется AlertDialogWrapper, который корректно обрабатывает focus trap и
          Esc для закрытия.
        </li>
        <li>
          Переключатель языка доступен с клавиатуры: Tab для фокуса, Enter/Space для переключения.
        </li>
        <li>
          Кнопки копирования между локалями имеют tooltips с описанием действия и доступны через
          Tab.
        </li>
        <li>Поля формы имеют правильные label для скринридеров.</li>
        <li>Автофокус на первое поле при открытии модального окна улучшает UX.</li>
        <li>
          Кнопка &quot;Сохранить&quot; disabled при невалидной форме → предотвращает отправку
          некорректных данных.
        </li>
        <li>Контрастность текста и ошибок валидации соответствует WCAG AA.</li>
      </ul>
    </FixtureSection>

    {/* 8. Используемые библиотеки */}
    <FixtureSection title="8. Используемые библиотеки" id="design-system-used">
      <FixtureLibsUsed
        items={[
          { label: "React", href: "https://react.dev/" },
          { label: "Next.js (App Router)", href: "https://nextjs.org/docs/app" },
          { label: "next-intl", href: "https://next-intl-docs.vercel.app/" },
          { label: "React Hook Form", href: "https://react-hook-form.com/" },
          { label: "Zod", href: "https://zod.dev/" },
          {
            label: "@hookform/resolvers",
            href: "https://github.com/react-hook-form/resolvers",
          },
          { label: "TipTap (rich editor)", href: "https://tiptap.dev/" },
          { label: "shadcn/ui — Button", href: "https://ui.shadcn.com/docs/components/button" },
          {
            label: "shadcn/ui — Alert Dialog",
            href: "https://ui.shadcn.com/docs/components/alert-dialog",
          },
          { label: "shadcn/ui — Form", href: "https://ui.shadcn.com/docs/components/form" },
          { label: "Tailwind CSS", href: "https://tailwindcss.com/docs" },
          { label: "Tabler Icons (@tabler/icons-react)", href: "https://tabler-icons.io/" },
        ]}
      />
    </FixtureSection>

    {/* 9. Рекомендации */}
    <FixtureSection title="9. Рекомендации" id="guidelines">
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Всегда передавайте валидную функцию patch для корректной работы сохранения. В production
          это должна быть настоящая API-функция.
        </li>
        <li>
          Используйте richEditor = true только для полей с HTML-контентом (fullDescription, plan).
          Для коротких полей (title) достаточно обычного textarea.
        </li>
        <li>
          Определяйте Zod-схему валидации через getValidationSchema с учётом бизнес-правил
          (минимальная/максимальная длина, обязательность).
        </li>
        <li>
          Для required полей используйте min=3 или больше, чтобы предотвратить случайное сохранение
          слишком коротких значений.
        </li>
        <li>
          Передавайте notes для полей, требующих дополнительного контекста (например, рекомендации
          по длине или формату).
        </li>
        <li>
          Используйте режим &quot;Все локали&quot; для редакторов контента и переводчиков, чтобы они
          могли видеть обе версии одновременно.
        </li>
        <li>
          Инструменты копирования между локалями (↓ и ↑) ускоряют перевод: можно скопировать
          оригинал и затем отредактировать.
        </li>
        <li>
          Не забывайте, что форма автоматически сбрасывается при закрытии без сохранения — это
          предотвращает случайные изменения.
        </li>
      </ul>
    </FixtureSection>

    {/* 10. Бизнес-правила */}
    <FixtureSection title="10. Бизнес‑правила" id="business-rules">
      <div className="space-y-3">
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Инвариант 1 (доступные локали): Локали определяются автоматически из initData по пути
            name через <code>contentTools.getNestedValue()</code>. Если поле отсутствует,
            используется дефолтный набор {`{ ru: "", en: "" }`}.
          </li>
          <li>
            Инвариант 2 (системная локаль): По умолчанию выбирается локаль системы (
            <code>useLocale()</code>), обеспечивая консистентность с языком интерфейса.
          </li>
          <li>
            Инвариант 3 (режим &quot;Все&quot;): При <code>locale = &quot;all&quot;</code>{" "}
            отображаются оба поля (ru и en) друг под другом с метками языка. Автофокус на первом
            поле.
          </li>
          <li>
            Инвариант 4 (копирование между локалями): Каждое поле имеет инструменты копирования (↓ и
            ↑) для переноса текста из одного языка в другой. Это ускоряет перевод.
          </li>
          <li>
            Инвариант 5 (минимальная отправка): Отправляются только изменённые поля через{" "}
            <code>contentTools.editActions.prepareModifiedValues()</code>, основываясь на{" "}
            <code>formState.dirtyFields</code>.
          </li>
          <li>
            Инвариант 6 (обработка конфликтов): Если сервер изменил те же поля, открывается{" "}
            <code>ConflictAlert</code> с списком конфликтующих полей. Пользователь может сбросить
            форму к актуальному состоянию сервера.
          </li>
          <li>
            Инвариант 7 (валидация): Zod-схема валидации применяется к каждому полю. Для richEditor
            валидация учитывает только видимый текст через <code>getSanitizedContent()</code>.
          </li>
          <li>
            Инвариант 8 (защита прав): Компонент рендерится только если{" "}
            <code>initData.rights?.canPatch = true</code>. Без прав возвращается null.
          </li>
          <li>
            Инвариант 9 (сброс при закрытии): При закрытии модального окна без сохранения форма
            автоматически сбрасывается к <code>initData</code> через <code>onCloseHandler</code>.
          </li>
          <li>
            Инвариант 10 (активность кнопки): Кнопка &quot;Сохранить&quot; активна только если{" "}
            <code>isDirty = true</code> (есть изменения) и <code>isValid = true</code> (валидация
            пройдена).
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 11. Примечания/допущения */}
    <FixtureSection title="11. Примечания/допущения" id="notes-assumptions">
      <div className="space-y-3">
        <p>
          <strong>Контекстуальные допущения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            Компонент использует React Hook Form (RHF) для управления состоянием формы.
            Предполагается, что провайдер формы настроен через <code>ContentEditBlock</code>.
          </li>
          <li>
            Валидация осуществляется через Zod с интеграцией <code>@hookform/resolvers/zod</code>.
            Сообщения об ошибках локализуются через next-intl.
          </li>
          <li>
            <code>contentTools.getNestedValue()</code> корректно извлекает вложенные значения из
            initData по пути name (например, &quot;content.data.title&quot;).
          </li>
          <li>
            <code>contentTools.editActions.prepareModifiedValues()</code> минимизирует payload,
            отправляя только изменённые поля. Это критично для предотвращения конфликтов при
            одновременном редактировании.
          </li>
          <li>
            <code>ContentEditBlock</code> обрабатывает <code>ConflictError</code> и открывает{" "}
            <code>ConflictAlert</code> с полями для сброса.
          </li>
          <li>
            Инструменты копирования между локалями (<code>LocaleTools</code>) используют{" "}
            <code>form.setValue()</code> с флагами <code>shouldDirty</code> и{" "}
            <code>shouldValidate</code> для корректного обновления состояния формы.
          </li>
          <li>
            Rich editor (TipTap) настроен на уровне <code>FormTextField</code> и активируется через
            проп <code>richEditor = true</code>.
          </li>
        </ul>
        <p>
          <strong>TODO/риски:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Проверить наличие переводов для кнопок копирования между локалями в namespace
            common.components.common.forms.formTextInputTools.
          </li>
          <li>
            Уточнить поведение при отсутствии одной из локалей в initData (текущая реализация
            использует дефолтный {`{ ru: "", en: "" }`}).
          </li>
          <li>
            Протестировать обработку конфликтов с реальным сервером для подтверждения корректности
            определения конфликтующих полей.
          </li>
          <li>
            Рассмотреть возможность добавления индикатора загрузки для асинхронной операции patch
            (текущая реализация полагается на AlertDialogWrapper).
          </li>
          <li>
            Подтвердить, что TipTap корректно обрабатывает очень длинный HTML-контент (10000+
            символов) без проблем с производительностью.
          </li>
        </ul>
      </div>
    </FixtureSection>
  </>
);

export default ContentLocalizableEditBlockFixture;

export const meta: DesignSystemFixtureMetadata = {
  group: "Proposals",
  parentItem: "Activity / Edit",
  title: "Content Localizable Edit Block",
  description:
    "Локализуемый блок редактирования контента с валидацией, конфликт-резолюцией и копированием между языками",
};
